import React, { useCallback, useEffect, useMemo, useState } from 'react';

interface NotebookCarouselProps {
  images: { src: string; alt: string }[];
  autoPlayDelay?: number;
  className?: string;
}

const NotebookCarousel: React.FC<NotebookCarouselProps> = ({
  images,
  autoPlayDelay = 4000,
  className = '',
}) => {
  const normalizedSlides = useMemo(
    () => images.filter((item) => Boolean(item?.src)),
    [images]
  );
  const totalSlides = normalizedSlides.length;
  const [index, setIndex] = useState(0);

  const goToNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const goToPrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  useEffect(() => {
    if (totalSlides <= 1) {
      return;
    }
    const intervalId = window.setInterval(goToNext, autoPlayDelay);
    return () => window.clearInterval(intervalId);
  }, [autoPlayDelay, goToNext, totalSlides]);

  useEffect(() => {
    if (index >= totalSlides) {
      setIndex(0);
    }
  }, [index, totalSlides]);

  if (totalSlides === 0) {
    return null;
  }

  return (
    <section className={`relative mx-auto w-full overflow-hidden rounded-3xl border border-white/10 bg-neutral-900 shadow-2xl ${className}`}>
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
        aria-live="polite"
      >
        {normalizedSlides.map((image, imageIndex) => (
          <div key={`${image.src}-${imageIndex}`} className="basis-full flex-shrink-0">
            <img
              src={image.src}
              alt={image.alt}
              className="h-full w-full object-cover"
              loading={imageIndex === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>
      {totalSlides > 1 && (
        <>
          <button
            type="button"
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Voir le visuel précédent"
          >
            ❮
          </button>
          <button
            type="button"
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Voir le visuel suivant"
          >
            ❯
          </button>
        </>
      )}
      {totalSlides > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
          {normalizedSlides.map((_, dotIndex) => (
            <span
              key={dotIndex}
              className={`h-2 w-2 rounded-full transition ${
                index === dotIndex ? 'bg-white' : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default NotebookCarousel;
